import { colors } from "../style";

// "4:12", "1:02:05"
export function formatSeconds(seconds) {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

// "45 sec", "12 min", "1 hr 5 min": for lists, where 12:04 reads as a time.
// null when the length is unknown, so callers can leave it out.
export function formatSpokenDuration(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Rounded first, so 59.7 reads "1 min", not "60 sec"
  const whole = Math.max(1, Math.round(n));
  if (whole < 60) return `${whole} sec`;
  const minutes = Math.round(whole / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

// Intl formatters are slow to build, so there's one per locale, options and
// zone. An unknown IANA zone falls back to the browser's.
const formatters = new Map();
function formatter(options, timeZone, locale) {
  const key = `${locale ?? ""}|${timeZone ?? ""}|${JSON.stringify(options)}`;
  let f = formatters.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    } catch {
      f = new Intl.DateTimeFormat(locale, options);
    }
    formatters.set(key, f);
  }
  return f;
}

const inZone = (options, timeZone, date) =>
  formatter(options, timeZone).format(date);

export const formatTime = (iso, timeZone) =>
  inZone({ timeStyle: "short" }, timeZone, new Date(iso));

// "Mon 2 Nov, 10:32" in `timeZone`, with the year when it isn't this one
export function formatDateTime(iso, timeZone) {
  const date = new Date(iso);
  const thisYear =
    zonedDay(date, timeZone).getFullYear() === new Date().getFullYear();
  return inZone(
    {
      weekday: "short",
      day: "numeric",
      month: "short",
      ...(thisYear ? {} : { year: "numeric" }),
      hour: "2-digit",
      minute: "2-digit",
    },
    timeZone,
    date,
  );
}

const startOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Local midnight of the calendar day `date` falls on in `timeZone`
function zonedDay(date, timeZone) {
  if (!timeZone) return new Date(startOfDay(date));
  const parts = Object.fromEntries(
    formatter(
      { year: "numeric", month: "numeric", day: "numeric" },
      timeZone,
      "en-CA",
    )
      .formatToParts(date)
      .map((p) => [p.type, Number(p.value)]),
  );
  return new Date(parts.year, parts.month - 1, parts.day);
}

// Whole calendar days from `date` to today in `timeZone` (0 = today,
// negative = later today or ahead by clock skew)
export const daysAgo = (date, timeZone) =>
  Math.round(
    (zonedDay(new Date(), timeZone) - zonedDay(new Date(date), timeZone)) /
      86400000,
  );

// Calendar day, for grouping
export function dayKey(iso, timeZone) {
  const d = zonedDay(new Date(iso), timeZone);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// "Today", "Yesterday", "Monday" within the week, else "12 October" (plus the
// year when it isn't this one)
export function formatDayHeading(iso, timeZone) {
  const days = daysAgo(iso, timeZone);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  // Local midnight of the zone's day, so these format in the browser's zone
  const date = zonedDay(new Date(iso), timeZone);
  if (days < 7) return formatter({ weekday: "long" }).format(date);
  const thisYear =
    date.getFullYear() === zonedDay(new Date(), timeZone).getFullYear();
  return formatter({
    day: "numeric",
    month: "long",
    ...(thisYear ? {} : { year: "numeric" }),
  }).format(date);
}

// Consecutive items on the same day share a group, in the order given. Mixed
// zones can bring a day back later, so repeats get a suffixed key.
export function groupByDay(items, isoFor, zoneFor = () => undefined) {
  const result = [];
  const seen = new Map();
  for (const item of items) {
    const iso = isoFor(item);
    const day = dayKey(iso, zoneFor(item));
    const last = result[result.length - 1];
    if (last?.day === day) {
      last.items.push(item);
      continue;
    }
    const n = seen.get(day) ?? 0;
    seen.set(day, n + 1);
    result.push({
      key: n ? `${day}#${n}` : day,
      day,
      title: formatDayHeading(iso, zoneFor(item)),
      items: [item],
    });
  }
  return result;
}

// The stand-ins the calls API writes for missing text (see the API's
// CallRecordingTranscription resource). Real text can be bracketed too, e.g.
// "[inaudible]", so only these count.
const API_PLACEHOLDERS = new Set([
  "[Generating transcription]",
  "[Generating summary]",
  "[Transcription too short to generate summary]",
  "[No summary available]",
  "[Transcription failed]",
  "[Call was not transcribed]",
  "[No speech detected in call]",
  "[No sentiment summary available]",
]);

// The stand-in's message without brackets, else null
export const apiPlaceholder = (text) =>
  typeof text === "string" && API_PLACEHOLDERS.has(text.trim())
    ? text.trim().slice(1, -1)
    : null;

// "Today, 11:30" or "Monday 2 November, 11:30"
export function formatMeetingDate(iso) {
  const days = daysAgo(iso);
  const day =
    days > 1 && days < 7
      ? formatter({ weekday: "long", day: "numeric", month: "long" }).format(
          new Date(iso),
        )
      : formatDayHeading(iso);
  return `${day}, ${formatTime(iso)}`;
}

// A short date for search results, in `timeZone`: "Today 11:30",
// "Yesterday 15:00", a weekday within the last 6 days ("Wed 28 Oct"), else
// "28 Oct", with the year when it isn't this one ("28 Oct 2025")
export function formatShortDate(iso, timeZone) {
  const days = daysAgo(iso, timeZone);
  if (days <= 0) return `Today ${formatTime(iso, timeZone)}`;
  if (days === 1) return `Yesterday ${formatTime(iso, timeZone)}`;
  const thisYear =
    zonedDay(new Date(iso), timeZone).getFullYear() ===
    zonedDay(new Date(), timeZone).getFullYear();
  return inZone(
    {
      ...(days < 7 ? { weekday: "short" } : {}),
      day: "numeric",
      month: "short",
      ...(thisYear ? {} : { year: "numeric" }),
    },
    timeZone,
    new Date(iso),
  );
}

export const CAPTURE = {
  in_person: { label: "In person", color: colors.green, icon: "users" },
  online_call: { label: "Online call", color: colors.call, icon: "video" },
};

export const captureFor = (capture) => CAPTURE[capture] ?? CAPTURE.in_person;

export const STATUS = {
  PENDING_UPLOAD: {
    label: "Waiting for upload",
    color: colors.amber,
    busy: true,
  },
  PENDING: { label: "Queued", color: colors.amber, busy: true },
  TRANSCRIBING: { label: "Transcribing", color: colors.amber, busy: true },
  SUMMARISING: { label: "Summarising", color: colors.amber, busy: true },
  PROCESSED: { label: "Ready", color: colors.green },
  FAILED: { label: "Failed", color: colors.red },
  DID_NOT_TRANSCRIBE: { label: "Not transcribed", color: colors.red },
};

export const PROGRESS_COPY = {
  PENDING_UPLOAD: "Waiting for the recording to upload",
  PENDING: "Queued for transcription…",
  TRANSCRIBING: "Transcribing…",
  SUMMARISING: "Summarising…",
  FAILED: "Couldn't be processed",
  DID_NOT_TRANSCRIBE: "Not transcribed",
};

// Sentiment rating (1 to 5) as a label and chip colour
export const SENTIMENT = {
  1: { label: "Very negative", color: colors.red, face: "angry" },
  2: { label: "Negative", color: colors.purple, face: "frown" },
  3: { label: "Neutral", color: "#98b6d9", face: "meh" },
  4: { label: "Positive", color: colors.teal, face: "smile" },
  5: { label: "Very positive", color: colors.green, face: "laugh" },
};

export const SPEAKER_COLOURS = [
  "#c3c8ee",
  "#9ee0de",
  "#e2c3ec",
  "#98d9a5",
  "#98b6d9",
  "#e8c56f",
];

// Hex or a non-transparent rgb()/rgba(). The calls API sends "rgba(0,0,0,0)"
// (and a malformed "rgba(0,0,0,0),") as a no-colour placeholder.
const RGB =
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*([\d.]+)\s*)?\)$/;
export function usableColour(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return true;
  const m = v.match(RGB);
  return !!m && (m[1] === undefined || Number(m[1]) > 0);
}

// The turn's own colour, else one per participant by first appearance
export function speakerColours(conversation) {
  const map = new Map();
  for (const b of conversation ?? []) {
    if (!map.has(b.participant)) {
      map.set(
        b.participant,
        usableColour(b.color)
          ? b.color
          : SPEAKER_COLOURS[map.size % SPEAKER_COLOURS.length],
      );
    }
  }
  return map;
}

// Index of the last turn that had started by `at` seconds, or -1
export function turnAt(conversation, at) {
  let index = -1;
  (conversation ?? []).forEach((b, i) => {
    if (b.start != null && b.start <= at) index = i;
  });
  return index;
}

// when: the date line, else recorded_at in the browser's zone
export function meetingAsText(m, when) {
  const date =
    when ?? (m.recorded_at ? formatMeetingDate(m.recorded_at) : null);
  const parts = [m.title || "Meeting", date].filter(Boolean);
  if (m.summary) parts.push("", m.summary);
  for (const note of m.meeting_notes ?? []) {
    const content = (note.content ?? []).filter(Boolean);
    parts.push("");
    if (note.header) parts.push(note.header);
    parts.push(...content.map((l) => (content.length > 1 ? `• ${l}` : l)));
  }
  if (m.user_notes) parts.push("", "Your notes", m.user_notes);
  return parts.join("\n");
}

// Drops repeats ignoring case, keeping the first spelling
export function uniqueEmails(emails) {
  const seen = new Set();
  return emails.filter((e) => {
    const key = e.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
